import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, ClipboardCheck, Eye, Pencil } from 'lucide-react'
import { Button, Pagination, TableActionMenu } from '../../../../components'
import { AVATAR_COLOR_CLASSES } from '../constants/hrmsAttendanceLeave.constants'
import type { StaffAttendanceRecord } from '../types/hrmsAttendanceRecords.types'
import { AttendanceRecordStatusBadge } from './AttendanceRecordStatusBadge'
import { AttendanceSourceBadge } from './AttendanceSourceBadge'

interface AttendanceRecordsTableProps {
  records: StaffAttendanceRecord[]
  filteredCount: number
  currentPage: number
  totalPages: number
  from: number
  to: number
  selectedDate?: string
  loading?: boolean
  onPageChange: (page: number) => void
  onViewRecord: (id: string) => void
  onEditRecord?: (record: StaffAttendanceRecord) => void
  onExportDailySheet?: () => void
}

function formatDatePill(dateStr?: string): string {
  if (!dateStr) return ''
  const parts = dateStr.split('-')
  if (parts.length === 3) {
    const [year, month, day] = parts
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const mIdx = parseInt(month, 10) - 1
    if (mIdx >= 0 && mIdx < 12) {
      return `${parseInt(day, 10)} ${months[mIdx]} ${year}`
    }
  }
  return dateStr
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function formatTime12h(timeStr?: string): string {
  if (!timeStr || timeStr === 'none') return '—'
  const [hStr, mStr] = timeStr.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (isNaN(h) || isNaN(m)) return timeStr
  const period = h >= 12 ? 'PM' : 'AM'
  const displayH = h % 12 === 0 ? 12 : h % 12
  const formattedH = displayH < 10 ? `0${displayH}` : `${displayH}`
  const formattedM = m < 10 ? `0${m}` : `${m}`
  return `${formattedH}:${formattedM} ${period}`
}

function formatWorkingHours(minutes?: number): string {
  if (minutes === undefined || minutes === null) return '—'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, 'ellipsis', totalPages]
  }
  if (currentPage >= totalPages - 2) {
    return [1, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  }
  return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages]
}

export function AttendanceRecordsTable({
  records,
  filteredCount,
  currentPage,
  totalPages,
  from,
  to,
  selectedDate,
  loading = false,
  onPageChange,
  onViewRecord,
  onEditRecord,
}: AttendanceRecordsTableProps) {
  const navigate = useNavigate()
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null)
  const pageNumbers = getPageNumbers(currentPage, totalPages)

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs min-w-0 space-y-4 font-sans">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-w-0">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <h2 className="text-base font-bold text-slate-900">Staff Attendance Records</h2>
          {selectedDate && (
            <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-sm font-medium px-3 py-1 rounded-full whitespace-nowrap">
              <Calendar className="w-4 h-4 text-gray-500" />
              {formatDatePill(selectedDate)}
            </span>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          icon={<ClipboardCheck className="h-4 w-4 text-[#146971]" />}
          onClick={() => navigate('/admin/hrms/attendance-leave/attendance/regularization-requests')}
          className="bg-white hover:bg-slate-50 text-[#146971] border border-slate-200 h-9 text-xs rounded-lg font-semibold shadow-xs whitespace-nowrap shrink-0 self-start sm:self-auto"
        >
          View Pending Regularization Requests
        </Button>
      </div>
      <div className="w-full overflow-x-auto min-w-0 rounded-xl border border-slate-200">
        <table className="w-full min-w-max text-left text-sm border-collapse table-auto">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold border-b border-slate-200">
              <th className="py-3 px-4 whitespace-nowrap">EMPLOYEE</th>
              <th className="py-3 px-4 whitespace-nowrap">EMPLOYEE ID</th>
              <th className="py-3 px-4 whitespace-nowrap">DEPARTMENT</th>
              <th className="py-3 px-4 whitespace-nowrap">CHECK IN</th>
              <th className="py-3 px-4 whitespace-nowrap">CHECK OUT</th>
              <th className="py-3 px-4 whitespace-nowrap">WORKING HOURS</th>
              <th className="py-3 px-4 whitespace-nowrap">STATUS</th>
              <th className="py-3 px-4 whitespace-nowrap">SOURCE</th>
              <th className="py-3 px-4 text-right whitespace-nowrap">ACTION</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={9} className="py-12 text-center text-slate-500 font-medium">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-5 w-5 border-2 border-[#146971] border-t-transparent rounded-full animate-spin" />
                    <span>Loading attendance records...</span>
                  </div>
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-8 text-center text-slate-500 font-medium whitespace-nowrap">
                  {selectedDate ? `No attendance records for ${formatDatePill(selectedDate)}` : 'No attendance records found'}
                </td>
              </tr>
            ) : (
              records.map((rec, index) => {
                const colorClass = AVATAR_COLOR_CLASSES[index % AVATAR_COLOR_CLASSES.length]
                const isLate = rec.status === 'Late'

                return (
                  <tr key={rec.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-full border flex items-center justify-center font-bold text-xs shrink-0 ${colorClass}`}
                        >
                          {getInitials(rec.name)}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 whitespace-nowrap">{rec.name}</div>
                          <div className="text-xs text-slate-500 whitespace-nowrap">{rec.designation}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">{rec.employeeId}</td>
                    <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">
                      {rec.department}
                    </td>
                    <td className={`py-3 px-4 font-medium whitespace-nowrap ${isLate ? 'text-amber-700 font-semibold' : 'text-slate-700'}`}>
                      {formatTime12h(rec.checkIn)}
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-medium whitespace-nowrap">
                      {formatTime12h(rec.checkOut)}
                    </td>
                    <td className="py-3 px-4 font-semibold text-slate-900 whitespace-nowrap">
                      {formatWorkingHours(rec.workingMinutes)}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <AttendanceRecordStatusBadge status={rec.status} />
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <AttendanceSourceBadge source={rec.source} />
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <TableActionMenu
                        ariaLabel="Row actions"
                        isOpen={openMenuRowId === rec.id}
                        onToggle={() =>
                          setOpenMenuRowId(openMenuRowId === rec.id ? null : rec.id)
                        }
                        onClose={() => setOpenMenuRowId(null)}
                        items={[
                          {
                            label: 'View',
                            icon: Eye,
                            onClick: () => onViewRecord(rec.id),
                          },
                          {
                            label: 'Edit',
                            icon: Pencil,
                            onClick: () => onEditRecord?.(rec),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="pt-2">
        <Pagination
          summary={
            <span>
              Showing <span className="font-bold">{from}</span>–<span className="font-bold">{to}</span> of{' '}
              <span className="font-bold">{filteredCount}</span> employees
            </span>
          }
          currentPage={currentPage}
          totalPages={totalPages}
          pages={pageNumbers}
          previousDisabled={currentPage === 1}
          nextDisabled={currentPage === totalPages || totalPages === 0}
          onPrevious={() => onPageChange(currentPage - 1)}
          onNext={() => onPageChange(currentPage + 1)}
          onPageChange={(p) => onPageChange(p)}
        />
      </div>
    </div>
  )
}
